import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useClientAuth } from "@/context/ClientAuthContext";
import api, { formatApiError } from "@/lib/api";
import { FamilyPetsFields } from "@/components/FamilyPetsFields";
import Seo from "@/components/Seo";

export default function ClientLogin() {
  const { login, register } = useClientAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", preferred_language: "en" });
  const [profile, setProfile] = useState({ preferred_name: "", party_adults: null, party_kids: null, personalization_notes: "", pets: [] });
  const [showProfile, setShowProfile] = useState(false);
  const [busy, setBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const FIELD = "w-full bg-white border border-sand-200 rounded-lg px-4 py-3 text-charcoal-800 focus:outline-none focus:ring-2 focus:ring-sage/50";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        navigate("/portal");
      } else if (mode === "register") {
        await register(form.name, form.email, form.password, form.preferred_language, profile);
        navigate("/portal");
      } else {
        const { data } = await api.post("/auth/forgot-password", { email: form.email, origin_url: window.location.origin });
        toast.success(data.message);
        setForgotSent(true);
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#F7F4EF] flex flex-col items-center justify-center px-6 py-16">
      <Seo title="Client sign in | Hola Movers" description="Sign in to view your relocation quotes and documents." noindex />
      <div className="flex items-center gap-2 mb-6">
        <img src="/brand/logo-mark.png" alt="Hola Movers" className="mx-auto h-10 w-auto" />
        <span className="font-serif text-xl text-charcoal-800">Hola Movers</span>
      </div>
      <div className="w-full max-w-md rounded-2xl bg-white border border-sand-200 shadow-sm p-8">
        <h1 className="font-serif text-2xl text-charcoal-800">
          {mode === "login" ? "Welcome back" : mode === "register" ? "Create your account" : "Reset your password"}
        </h1>
        <p className="text-sm text-charcoal-500 mt-1 mb-6">
          {mode === "login" ? "Sign in to see your quotes, bookings, and documents."
            : mode === "register" ? "Register to track your move with Hola Movers."
            : "Enter your email and we'll send you a reset link."}
        </p>
        {mode === "forgot" && forgotSent ? (
          <p data-testid="forgot-sent" className="rounded-xl bg-sage/10 px-4 py-3 text-sm text-charcoal-700">
            If an account exists for that email, a reset link is on its way. Check your inbox (and spam folder).
          </p>
        ) : (
        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <input data-testid="client-name" className={FIELD} placeholder="Your name" value={form.name}
              onChange={(e) => set("name", e.target.value)} required />
          )}
          <input data-testid="client-email" type="email" className={FIELD} placeholder="Email" value={form.email}
            onChange={(e) => set("email", e.target.value)} required />
          {mode !== "forgot" && (
            <input data-testid="client-password" type="password" className={FIELD} placeholder="Password (min 8 chars)" value={form.password}
              onChange={(e) => set("password", e.target.value)} required />
          )}
          {mode === "register" && (
            <div>
              <label className="text-sm text-charcoal-500 mb-1 block">Email me in</label>
              <select data-testid="register-language" className={FIELD} value={form.preferred_language}
                onChange={(e) => set("preferred_language", e.target.value)}>
                <option value="en">English</option>
                <option value="es">Español</option>
              </select>
            </div>
          )}
          {mode === "register" && (
            <div>
              <button type="button" data-testid="register-profile-toggle" onClick={() => setShowProfile((v) => !v)}
                className="text-sm text-terracotta hover:underline">
                {showProfile ? "Hide" : "Optional: tell us about you & your pets 🐾"}
              </button>
              {showProfile && (
                <div className="mt-3 rounded-xl border border-sand-200 p-4">
                  <FamilyPetsFields value={profile} onChange={setProfile} compact />
                </div>
              )}
            </div>
          )}
          <button data-testid="client-submit" type="submit" disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-terracotta px-6 py-3 text-white font-medium hover:bg-terracotta-hover disabled:opacity-60">
            {busy ? <Loader2 size={16} className="animate-spin" /> : null}
            {mode === "login" ? "Sign in" : mode === "register" ? "Create account" : "Send reset link"}
          </button>
        </form>
        )}
        <div className="mt-5 flex flex-col items-start gap-2">
          <button data-testid="client-toggle-mode" onClick={() => { setMode(mode === "login" ? "register" : "login"); setForgotSent(false); }}
            className="text-sm text-terracotta hover:underline">
            {mode === "login" ? "New here? Create an account" : "Already have an account? Sign in"}
          </button>
          {mode === "login" && (
            <button data-testid="client-forgot-link" onClick={() => { setMode("forgot"); setForgotSent(false); }}
              className="text-sm text-charcoal-500 hover:underline">
              Forgot your password?
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
